/**
 * AddSectionPickerDialog — modal para elegir el tipo del nuevo nodo.
 *
 * Reemplaza al dropdown chico que había antes. Layout inspirado en el
 * prototipo: header con eyebrow del template + título; search input;
 * cuerpo scrollable agrupado por familia (Contenido fijo, Texto, Listas,
 * Tabla, Diagramas, Código, Archivos); cada type es una card grande con
 * icono + nombre + descripción + value canónico; selección click-to-pick
 * con confirmación en footer (Cancelar / Continuar).
 *
 * Tipos `available: false` se renderizan en gris con el `pendingTag`
 * (ej. "Commit E", "B.7", "v2") para que el user vea el catálogo
 * completo y entienda qué falta.
 *
 * Búsqueda: filtra por name, description y value (string match
 * case-insensitive). Si no hay resultados, muestra empty state.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Boxes,
  CalendarRange,
  CircleDot,
  Code2,
  Database,
  FileQuestion,
  Folder,
  Footprints,
  GitBranch,
  GitFork,
  Image as ImageIcon,
  LayoutGrid,
  List,
  ListChecks,
  ListOrdered,
  MessageSquareQuote,
  Network,
  Paperclip,
  Quote,
  Search,
  StickyNote,
  TableProperties,
  Type,
  UsersRound,
  Workflow,
  X,
  type LucideIcon,
} from 'lucide-react'

import type { BlockType } from '../../../api/templates'
import {
  groupBlockTypesByFamily,
  type BlockTypeMeta,
} from './blockTypeCatalog'

interface AddSectionPickerDialogProps {
  open: boolean
  templateName: string
  onClose: () => void
  onPick: (blockType: BlockType) => void
}

/** Icono Lucide por tipo. Cuando un tipo nuevo aparece sin entry aquí,
 * cae al fallback `FileQuestion`. */
const ICON_MAP: Partial<Record<BlockType, LucideIcon>> = {
  // fixed
  fixed_heading: Folder,
  fixed_block: StickyNote,
  // text
  rich_text: MessageSquareQuote,
  plain_text: Type,
  key_value_grid: LayoutGrid,
  // list
  bullet_list: List,
  numbered_list: ListOrdered,
  checklist: ListChecks,
  // table
  structured_table: TableProperties,
  // diagram
  diagram: GitBranch,
  mermaid_flowchart: GitFork,
  mermaid_sequence: Workflow,
  mermaid_use_case: UsersRound,
  mermaid_activity: Workflow,
  mermaid_state: CircleDot,
  mermaid_er: Database,
  mermaid_class: Boxes,
  mermaid_gantt: CalendarRange,
  mermaid_journey: Footprints,
  mermaid_mindmap: Network,
  mermaid_generic: GitBranch,
  // code
  code_block: Code2,
  structured: Code2,
  // files
  image_upload: ImageIcon,
  file_upload: Paperclip,
  embed_canon: Quote,
  // fallback covered below
}

export function AddSectionPickerDialog(props: AddSectionPickerDialogProps) {
  // El contenido se monta al abrir: cada apertura arranca con la búsqueda
  // y la selección vacías, sin reiniciarlas con efectos.
  if (!props.open) return null
  return <PickerContent {...props} />
}

function PickerContent({
  templateName,
  onClose,
  onPick,
}: AddSectionPickerDialogProps) {
  const { t } = useTranslation('templates')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<BlockType | null>(null)

  // Bloqueo de scroll + Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  const groups = useMemo(() => {
    const all = groupBlockTypesByFamily()
    // Siempre filtrar tipos deprecados — el picker solo lista lo que
    // se puede crear (available) o lo que está por venir (Commit E /
    // B.7 / v2 con pendingTag visible). Tipos deprecados existen en
    // el catálogo solo para que las plantillas legacy sigan
    // renderizando; no aparecen como opción de creación.
    const noDeprecated = all
      .map((g) => ({
        family: g.family,
        types: g.types.filter((t) => t.pendingTag !== 'deprecada'),
      }))
      .filter((g) => g.types.length > 0)

    if (!query.trim()) return noDeprecated
    const q = query.trim().toLowerCase()
    return noDeprecated
      .map((g) => ({
        family: g.family,
        types: g.types.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            t.description.toLowerCase().includes(q) ||
            t.value.toLowerCase().includes(q) ||
            t.shortLabel.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.types.length > 0)
  }, [query])

  const selectedMeta = selected
    ? groups.flatMap((g) => g.types).find((t) => t.value === selected) ?? null
    : null

  function commit() {
    if (!selectedMeta || !selectedMeta.available) return
    onPick(selectedMeta.value)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 backdrop-blur-sm overflow-y-auto py-12"
      role="dialog"
      aria-modal="true"
      aria-label={t('designer.picker.dialog_title')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="mx-4 w-full max-w-3xl rounded-md border border-line bg-surface shadow-lg flex flex-col max-h-[calc(100vh-6rem)]">
        {/* Header */}
        <header className="px-6 pt-5 pb-4 border-b border-line">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
                {t('designer.picker.eyebrow', { name: templateName })}
              </p>
              <h2 className="mt-1 font-display text-xl font-semibold text-ink">
                {t('designer.picker.dialog_title')}
              </h2>
              <p className="mt-1 text-xs text-ink-muted">
                {t('designer.picker.dialog_subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('designer.picker.close')}
              className="p-1 rounded text-ink-subtle hover:text-ink hover:bg-surface-muted shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Search */}
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('designer.picker.search_placeholder')}
              className="w-full pl-9 pr-3 py-2 bg-canvas border border-line rounded-md text-sm focus:outline-none focus:border-brand"
              autoFocus
            />
          </div>
        </header>

        {/* Body — grouped by family */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {groups.length === 0 ? (
            <EmptyState query={query} />
          ) : (
            <div className="space-y-6">
              {groups.map((g) => (
                <FamilySection
                  key={g.family.key}
                  title={t(g.family.titleKey)}
                  subtitle={t(g.family.subtitleKey)}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {g.types.map((type) => (
                      <TypeCard
                        key={type.value}
                        type={type}
                        selected={selected === type.value}
                        onSelect={() => {
                          if (!type.available) return
                          setSelected(type.value)
                        }}
                      />
                    ))}
                  </div>
                </FamilySection>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-line px-6 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-ink-muted min-w-0 truncate">
            {selectedMeta ? (
              <>
                {t('designer.picker.selected_label')}{' '}
                <span className="text-ink">{selectedMeta.name}</span>{' '}
                <span className="font-mono text-[11px] text-ink-subtle">
                  · {selectedMeta.value}
                </span>
              </>
            ) : (
              t('designer.picker.no_selection_hint')
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md"
            >
              {t('designer.picker.cancel')}
            </button>
            <button
              type="button"
              onClick={commit}
              disabled={!selectedMeta || !selectedMeta.available}
              className="px-5 py-2 bg-brand hover:bg-brand/90 text-canvas text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {t('designer.picker.continue')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────
function FamilySection({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: ReactNode
}) {
  return (
    <section>
      <header className="mb-2">
        <h3 className="font-display text-sm font-semibold text-ink inline">
          {title}
        </h3>{' '}
        <span className="text-xs text-ink-subtle">— {subtitle}</span>
      </header>
      {children}
    </section>
  )
}

function TypeCard({
  type,
  selected,
  onSelect,
}: {
  type: BlockTypeMeta
  selected: boolean
  onSelect: () => void
}) {
  const Icon = ICON_MAP[type.value] ?? FileQuestion
  const baseClass =
    'group rounded-md border p-3 text-left flex items-start gap-3 transition-colors'
  const stateClass = !type.available
    ? 'border-line bg-surface-muted/30 opacity-60 cursor-not-allowed'
    : selected
      ? 'border-brand bg-brand-soft/40 cursor-pointer'
      : 'border-line bg-canvas hover:border-line-strong hover:bg-surface-muted/40 cursor-pointer'

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!type.available}
      aria-pressed={selected}
      title={type.description}
      className={`${baseClass} ${stateClass}`}
    >
      <span
        className={
          type.available
            ? 'shrink-0 w-9 h-9 rounded-md bg-surface-muted flex items-center justify-center text-ink-muted group-hover:text-ink'
            : 'shrink-0 w-9 h-9 rounded-md bg-surface-muted/60 flex items-center justify-center text-ink-subtle'
        }
      >
        <Icon className="w-4 h-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2 flex-wrap">
          <span className="font-display text-sm font-semibold text-ink">
            {type.name}
          </span>
          {type.pendingTag ? (
            <span className="font-mono text-[9px] uppercase tracking-wider px-1 rounded bg-surface-muted text-ink-subtle">
              {type.pendingTag}
            </span>
          ) : null}
        </span>
        <span className="block mt-0.5 text-xs text-ink-muted leading-snug">
          {type.description}
        </span>
        <span className="block mt-1 font-mono text-[10px] text-ink-subtle">
          {type.value}
        </span>
      </span>
    </button>
  )
}

function EmptyState({ query }: { query: string }) {
  const { t } = useTranslation('templates')
  return (
    <div className="py-12 text-center">
      <p className="font-body text-sm text-ink-muted">
        {t('designer.picker.empty_search', { query })}
      </p>
    </div>
  )
}
